import React, { memo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import casinoWS from '../services/websocket';
import './RewardsDropdown.css';

const EMPTY_ARRAY = [];

// Premium Gift icon with gradients and animated indicator light (matching printer style)
const GiftIcon = ({ hasReward = false }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    {/* Ribbon bow - left */}
    <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" fill="url(#rewardRibbonGradient)" stroke="#b45309" strokeWidth="0.5"/>
    {/* Ribbon bow - right */}
    <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" fill="url(#rewardRibbonGradient)" stroke="#b45309" strokeWidth="0.5"/>
    {/* Box lid */}
    <rect x="2" y="7" width="20" height="5" rx="1" fill="url(#rewardBoxGradient)" stroke="currentColor" strokeWidth="1.5"/>
    {/* Box body */}
    <path d="M4 12v9a1 1 0 001 1h14a1 1 0 001-1v-9" fill="url(#rewardBoxBodyGradient)" stroke="currentColor" strokeWidth="1.5"/>
    {/* Ribbon vertical */}
    <rect x="10.5" y="7" width="3" height="15" fill="url(#rewardRibbonGradient)" opacity="0.9"/>
    {/* Indicator light - like printer LED */}
    <circle cx="17" cy="9.5" r="1.2" fill={hasReward ? "#d4981c" : "#475569"}>
      {hasReward && (
        <animate attributeName="opacity" values="1;0.4;1" dur="1.5s" repeatCount="indefinite"/>
      )}
    </circle>
    {/* Secondary indicator */}
    <circle cx="19" cy="9.5" r="0.8" fill={hasReward ? "#f59e0b" : "#374151"}>
      {hasReward && (
        <animate attributeName="opacity" values="0.4;1;0.4" dur="1.5s" repeatCount="indefinite"/>
      )}
    </circle>
    {/* Gradients */}
    <defs>
      <linearGradient id="rewardRibbonGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#d4981c"/>
        <stop offset="100%" stopColor="#f59e0b"/>
      </linearGradient>
      <linearGradient id="rewardBoxGradient" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#334155"/>
        <stop offset="100%" stopColor="#1e293b"/>
      </linearGradient>
      <linearGradient id="rewardBoxBodyGradient" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#1e293b"/>
        <stop offset="100%" stopColor="#0f172a"/>
      </linearGradient>
    </defs>
  </svg>
);

// Wheel icon for daily reward entry
const WheelIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="3" />
    <line x1="12" y1="2" x2="12" y2="6" />
    <line x1="12" y1="18" x2="12" y2="22" />
    <line x1="2" y1="12" x2="6" y2="12" />
    <line x1="18" y1="12" x2="22" y2="12" />
  </svg>
);

// Buff icons
const ZapIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const BuildingIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="10" width="4" height="11" rx="1" /><rect x="10" y="5" width="4" height="16" rx="1" /><rect x="17" y="1" width="4" height="20" rx="1" />
  </svg>
);

const SparklesIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" />
    <path d="M5 19l1 3 1-3 3-1-3-1-1-3-1 3-3 1 3 1z" />
  </svg>
);

// Get buff icon by type
const getBuffIcon = (iconType) => {
  switch (iconType) {
    case 'zap':
      return <ZapIcon />;
    case 'building':
      return <BuildingIcon />;
    case 'sparkles':
      return <SparklesIcon />;
    default:
      return <ZapIcon />;
  }
};

// Format duration
const formatDuration = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h${minutes > 0 ? ` ${minutes}m` : ''}`;
  return `${minutes}m`;
};

/**
 * RewardsDropdown Component
 * Shows a gift icon button with notification badge when rewards are available.
 * Dropdown menu with "Daily Reward" and "Buffs" entries.
 *
 * pendingBuffs is managed centrally by App.js via WebSocket events.
 * This component receives data through props only.
 */
const RewardsDropdown = ({ canSpin = false, onOpenWheel, pendingBuffs = EMPTY_ARRAY, onActivateBuff }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [activatingBuff, setActivatingBuff] = useState(null);
  const dropdownRef = useRef(null);

  const hasBuffs = pendingBuffs.length > 0;
  const hasRewards = canSpin || hasBuffs;
  // Total count of available rewards (daily + buffs)
  const totalRewardsCount = (canSpin ? 1 : 0) + pendingBuffs.length;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    const willOpen = !isOpen;
    setIsOpen(willOpen);
    // Request fresh pending buffs when opening
    if (willOpen && casinoWS.isConnected) {
      casinoWS.send({ type: 'getPendingBuffs' });
    }
  };

  const handleDailyRewardClick = () => {
    setIsOpen(false);
    if (onOpenWheel) {
      onOpenWheel();
    }
  };

  const handleBuffClick = async (buff) => {
    if (activatingBuff) return; // Prevent double-click
    setActivatingBuff(buff.id);
    if (onActivateBuff) {
      await onActivateBuff(buff);
    }
    setActivatingBuff(null);
    setIsOpen(false);
  };

  return (
    <div className="rewards-dropdown" ref={dropdownRef}>
      <button
        className={`rewards-btn ${hasRewards ? 'has-reward' : ''}`}
        onClick={handleToggle}
        title={t('rewards_title')}
      >
        <GiftIcon hasReward={hasRewards} />
        {hasRewards && <span className="reward-badge">{totalRewardsCount}</span>}
      </button>

      {isOpen && (
        <div className="rewards-dropdown-menu">
          <div className="rewards-dropdown-header">
            <span>{t('rewards_title')}</span>
          </div>

          <div className="rewards-dropdown-content">
            {/* Daily Reward */}
            <button
              className={`rewards-dropdown-item ${canSpin ? 'available' : ''}`}
              onClick={handleDailyRewardClick}
            >
              <div className="reward-item-icon">
                <WheelIcon />
              </div>
              <div className="reward-item-content">
                <span className="reward-item-title">{t('fortune_daily_reward')}</span>
                <span className="reward-item-status">
                  {canSpin ? t('rewards_spin_available') : t('rewards_on_cooldown')}
                </span>
              </div>
              {canSpin && (
                <span className="reward-item-indicator" />
              )}
            </button>

            {/* Buffs Section */}
            {hasBuffs && (
              <>
                <div className="rewards-dropdown-divider" />
                <div className="rewards-dropdown-section">
                  <span>{t('rewards_buffs_section')} ({pendingBuffs.length})</span>
                </div>
                {pendingBuffs.map((buff) => (
                  <button
                    key={buff.id}
                    className={`rewards-dropdown-item available buff-item ${activatingBuff === buff.id ? 'activating' : ''}`}
                    onClick={() => handleBuffClick(buff)}
                    disabled={activatingBuff === buff.id}
                    style={{ '--buff-color': '#10b981' }}
                  >
                    <div className="reward-item-icon" style={{ color: '#10b981' }}>
                      {getBuffIcon(buff.icon)}
                    </div>
                    <div className="reward-item-content">
                      <span className="reward-item-title" style={{ color: '#10b981' }}>
                        {buff.name}
                      </span>
                      <span className="reward-item-status">
                        {activatingBuff === buff.id ? t('activating') : `+${buff.value}% • ${formatDuration(buff.durationSeconds)}`}
                      </span>
                    </div>
                    <span className="reward-item-indicator" style={{ background: '#10b981' }} />
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(RewardsDropdown);
